import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Box, Button, InputAdornment, Stack, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  DataGrid,
  type DataGridProps,
  type GridColDef,
  type GridSortModel,
  type GridPaginationModel,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { GRID_PAGE_SIZES } from '../grid/usePersistedGridState';

export type SortOrder = 'asc' | 'desc';

/**
 * Estado de consulta server-side. Nomes em inglês — a tradução para os query
 * params PT-BR do GeneXus (`Pagina`, `Porpagina`, `Busca`, `Ordenarpor`,
 * `Ordem`) deve ser feita no `httpClient`/feature, não aqui no componente.
 *
 * - `page` é **1-based** (compatível com o backend).
 */
export interface DataTableQuery {
  page: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  sortOrder?: SortOrder;
  /**
   * Quando `true`, a API retorna também registros com soft-delete / `ativo=false`.
   * Tradução em `shared/api/genexus-query.ts` envia `incluirInativos=true` upstream.
   * Default omitido = só ativos.
   */
  includeInactive?: boolean;
}

export interface DataTableProps<T extends { id: string | number }> {
  rows: T[];
  columns: GridColDef<T>[];
  /** Total absoluto retornado pelo backend (não somente página atual). */
  total: number;
  loading?: boolean;
  query: DataTableQuery;
  onQueryChange: (next: DataTableQuery) => void;
  /** Slot de filtros adicionais (combos, datas) acima do grid. */
  filters?: ReactNode;
  /** Slot de ações primárias (ex.: botão "Novo") à direita da busca. */
  toolbarActions?: ReactNode;
  /** Placeholder da busca textual. Default i18n `components.dataTable.searchPlaceholder`. */
  searchPlaceholder?: string;
  /** Esconder caixa de busca textual. */
  hideSearch?: boolean;
  /** Page sizes permitidos. Default 10, 20, 50 e 100. */
  pageSizeOptions?: number[];
  /** Zera filtros, busca, ordenação e volta a quantidade padrão. */
  onClear?: () => void;
  /** Debounce do search em ms. Default 300. */
  searchDebounceMs?: number;
  /** Props extras repassadas ao DataGrid. */
  gridProps?: Partial<DataGridProps<T>>;
}

/**
 * Wrapper de `@mui/x-data-grid` com paginação/ordenação **server-side** e
 * busca textual com debounce. O estado da query é controlado externamente
 * via `DataTableQuery` (ver typedoc do tipo para convenções de naming).
 */
export function DataTable<T extends { id: string | number }>({
  rows,
  columns,
  total,
  loading,
  query,
  onQueryChange,
  filters,
  toolbarActions,
  searchPlaceholder,
  hideSearch,
  pageSizeOptions = [...GRID_PAGE_SIZES],
  onClear,
  searchDebounceMs = 300,
  gridProps,
}: DataTableProps<T>): JSX.Element {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState(query.search ?? '');

  // Sincroniza input com mudança externa de query (ex.: reset de filtros).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSearchInput(query.search ?? '');
  }, [query.search]);

  useEffect(() => {
    if ((query.search ?? '') === searchInput) return;
    const handle = setTimeout(() => {
      onQueryChange({ ...query, page: 1, search: searchInput || undefined });
    }, searchDebounceMs);
    return () => clearTimeout(handle);
    // onQueryChange/query são intencionalmente omitidos para não disparar a cada digito;
    // o efeito só precisa observar o input em si.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, searchDebounceMs]);

  const paginationModel: GridPaginationModel = useMemo(
    () => ({ page: Math.max(0, query.page - 1), pageSize: query.pageSize }),
    [query.page, query.pageSize],
  );

  const sortModel: GridSortModel = useMemo(
    () => (query.sortBy ? [{ field: query.sortBy, sort: query.sortOrder ?? 'asc' }] : []),
    [query.sortBy, query.sortOrder],
  );

  /** Ações de linha ficam sempre na primeira coluna, à esquerda da grade. */
  const orderedColumns = useMemo(() => {
    const isAction = (field: GridColDef<T>['field']): boolean =>
      field === 'actions' || field === 'acoes';
    const actionColumns = columns.filter((column) => isAction(column.field));
    if (actionColumns.length === 0) return columns;
    return [...actionColumns, ...columns.filter((column) => !isAction(column.field))];
  }, [columns]);

  return (
    <Box>
      {(!hideSearch || filters || toolbarActions || onClear) && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
          sx={{ mb: 2 }}
        >
          {!hideSearch && (
            <TextField
              size="small"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={searchPlaceholder ?? t('components.dataTable.searchPlaceholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 240 }}
              inputProps={{
                'aria-label': searchPlaceholder ?? t('components.dataTable.searchPlaceholder'),
              }}
            />
          )}
          {filters}
          {onClear && (
            <Button size="small" onClick={onClear} sx={{ alignSelf: 'flex-start', mt: 0.5, flexShrink: 0 }}>
              {t('components.dataTable.clearFilters')}
            </Button>
          )}
          {toolbarActions ? <Box sx={{ flex: 1 }} /> : null}
          {toolbarActions}
        </Stack>
      )}

      <DataGrid<T>
        rows={rows}
        columns={orderedColumns}
        loading={loading}
        rowCount={total}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        pageSizeOptions={pageSizeOptions}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => {
          onQueryChange({
            ...query,
            page: model.page + 1,
            pageSize: model.pageSize,
          });
        }}
        sortModel={sortModel}
        onSortModelChange={(model) => {
          const first = model[0];
          onQueryChange({
            ...query,
            page: 1,
            sortBy: first?.field,
            sortOrder: (first?.sort as SortOrder | null | undefined) ?? undefined,
          });
        }}
        disableColumnFilter
        disableRowSelectionOnClick
        /*
         * MUI x-data-grid v7 trunca colunas fora do viewport inicial mesmo com
         * scroll horizontal disponível. Descoberto na grid de NF Empresa e
         * confirmado em Empresas. Como todas as grids do projeto usam page size
         * ≤ 100, desligar a virtualização não pesa e evita o bug em todas elas.
         */
        disableVirtualization
        localeText={{
          noRowsLabel: t('components.dataTable.empty'),
          MuiTablePagination: {
            labelRowsPerPage: t('components.dataTable.rowsPerPage'),
            labelDisplayedRows: ({ from, to, count }) =>
              t('components.dataTable.displayedRows', { from, to, count }),
          },
        }}
        sx={{ minHeight: 360 }}
        {...gridProps}
      />
    </Box>
  );
}
